import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  type DocumentData,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db } from '@/lib/firebase/config'
import { functions } from '@/lib/firebase/config'
import { createAuditLog, getChangedFields, toDate } from '@/lib/firebase/firestore'
import type { Authorization, AuthorizationStatus, UserRole, Visit } from '@/types'

const COLLECTION = 'authorizations'

export type AuthorizationRenewalBand = 'expired' | '30' | '60' | '90'
export type AuthorizationExpirationMilestone = 90 | 60 | 30 | 0

type AuthorizationUtilizationSource = Pick<Authorization, 'unitsAuthorized' | 'unitsUsed'> &
  Partial<Pick<Authorization, 'limitation' | 'limitationValue' | 'startDate' | 'endDate'>>

interface AuthorizationWindow {
  rangeStart?: Date
  rangeEnd?: Date
}

const DAY_MS = 24 * 60 * 60 * 1000

function startOfDay(date: Date): Date {
  const value = new Date(date)
  value.setHours(0, 0, 0, 0)
  return value
}

function endOfDay(date: Date): Date {
  const value = new Date(date)
  value.setHours(23, 59, 59, 999)
  return value
}

function addDays(date: Date, days: number): Date {
  const value = new Date(date)
  value.setDate(value.getDate() + days)
  return value
}

function addMonthsClamped(date: Date, months: number): Date {
  const value = new Date(date)
  const day = value.getDate()
  value.setDate(1)
  value.setMonth(value.getMonth() + months)
  const lastDay = new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate()
  value.setDate(Math.min(day, lastDay))
  return value
}

function addYearsClamped(date: Date, years: number): Date {
  const value = new Date(date)
  const month = value.getMonth()
  const day = value.getDate()
  value.setFullYear(value.getFullYear() + years, month, 1)
  const lastDay = new Date(value.getFullYear(), month + 1, 0).getDate()
  value.setDate(Math.min(day, lastDay))
  return value
}

function getRecurringBaseUnits(auth: AuthorizationUtilizationSource): number {
  return Number(auth.limitationValue || auth.unitsAuthorized) || 0
}

export function isRecurringAuthorization(auth: AuthorizationUtilizationSource): boolean {
  return auth.limitation === 'Per Day' ||
    auth.limitation === 'Per Week' ||
    auth.limitation === 'Per Month' ||
    auth.limitation === 'Per Year'
}

function getCycleStart(auth: AuthorizationUtilizationSource, date: Date): Date | null {
  const authStart = toDate(auth.startDate)
  if (!authStart) return null

  const normalizedAuthStart = startOfDay(authStart)
  const normalizedDate = startOfDay(date)

  switch (auth.limitation) {
    case 'Per Day':
      return normalizedDate
    case 'Per Week': {
      const diffDays = Math.floor((normalizedDate.getTime() - normalizedAuthStart.getTime()) / DAY_MS)
      return addDays(normalizedAuthStart, Math.floor(Math.max(diffDays, 0) / 7) * 7)
    }
    case 'Per Month': {
      let months = (normalizedDate.getFullYear() - normalizedAuthStart.getFullYear()) * 12 +
        (normalizedDate.getMonth() - normalizedAuthStart.getMonth())
      let candidate = addMonthsClamped(normalizedAuthStart, Math.max(months, 0))
      if (candidate > normalizedDate) {
        months -= 1
        candidate = addMonthsClamped(normalizedAuthStart, Math.max(months, 0))
      }
      return candidate
    }
    case 'Per Year': {
      let years = normalizedDate.getFullYear() - normalizedAuthStart.getFullYear()
      let candidate = addYearsClamped(normalizedAuthStart, Math.max(years, 0))
      if (candidate > normalizedDate) {
        years -= 1
        candidate = addYearsClamped(normalizedAuthStart, Math.max(years, 0))
      }
      return candidate
    }
    default:
      return normalizedAuthStart
  }
}

function getNextCycleStart(auth: AuthorizationUtilizationSource, cycleStart: Date): Date {
  switch (auth.limitation) {
    case 'Per Day':
      return addDays(cycleStart, 1)
    case 'Per Week':
      return addDays(cycleStart, 7)
    case 'Per Month':
      return addMonthsClamped(cycleStart, 1)
    case 'Per Year':
      return addYearsClamped(cycleStart, 1)
    default:
      return endOfDay(toDate(auth.endDate) || cycleStart)
  }
}

export function getAuthorizationUtilizationWindow(
  auth: AuthorizationUtilizationSource,
  now: Date = new Date()
): { rangeStart?: Date; rangeEnd?: Date } {
  const authStart = toDate(auth.startDate)
  const authEnd = toDate(auth.endDate)
  if (!authStart || !authEnd) return {}

  if (!isRecurringAuthorization(auth)) {
    return { rangeStart: startOfDay(authStart), rangeEnd: endOfDay(authEnd) }
  }

  const referenceDate = now < authStart ? authStart : now > authEnd ? authEnd : now
  const cycleStart = getCycleStart(auth, referenceDate)
  if (!cycleStart) return { rangeStart: startOfDay(authStart), rangeEnd: endOfDay(authEnd) }

  const cycleEnd = new Date(getNextCycleStart(auth, cycleStart).getTime() - 1)

  return {
    rangeStart: cycleStart < authStart ? startOfDay(authStart) : cycleStart,
    rangeEnd: cycleEnd > authEnd ? endOfDay(authEnd) : cycleEnd,
  }
}

export function calculateAuthorizedUnitsForWindow(
  auth: AuthorizationUtilizationSource,
  options: AuthorizationWindow = {}
): number {
  if (!isRecurringAuthorization(auth)) {
    return Number(auth.unitsAuthorized) || 0
  }

  const authStart = toDate(auth.startDate)
  const authEnd = toDate(auth.endDate)
  if (!authStart || !authEnd) return getRecurringBaseUnits(auth)

  const effectiveStart = startOfDay(options.rangeStart && options.rangeStart > authStart ? options.rangeStart : authStart)
  const effectiveEnd = endOfDay(options.rangeEnd && options.rangeEnd < authEnd ? options.rangeEnd : authEnd)
  if (effectiveEnd < effectiveStart) return 0

  const baseUnits = getRecurringBaseUnits(auth)
  let cycleStart = getCycleStart(auth, effectiveStart)
  if (!cycleStart) return baseUnits

  let totalUnits = 0
  while (cycleStart <= effectiveEnd) {
    totalUnits += baseUnits
    cycleStart = getNextCycleStart(auth, cycleStart)
  }

  return totalUnits
}

export function calculateAuthorizationUtilization(
  auth: AuthorizationUtilizationSource,
  options: AuthorizationWindow & { unitsUsed?: number } = {}
): {
  utilizationPercent: number
  displayUtilizationPercent: number
  unitsAuthorized: number
  unitsUsed: number
  unitsRemaining: number
} {
  const unitsAuthorized = calculateAuthorizedUnitsForWindow(auth, options)
  const unitsUsed = Number(options.unitsUsed ?? auth.unitsUsed) || 0
  const unitsRemaining = Math.max(unitsAuthorized - unitsUsed, 0)
  const utilizationPercent = unitsAuthorized > 0
    ? Math.round((unitsUsed / unitsAuthorized) * 100)
    : 0

  return {
    utilizationPercent,
    displayUtilizationPercent: Math.min(100, utilizationPercent),
    unitsAuthorized,
    unitsUsed,
    unitsRemaining,
  }
}

export function resolveAuthorizationStatus(
  auth: AuthorizationUtilizationSource & { status?: AuthorizationStatus }
): AuthorizationStatus {
  const endDate = toDate(auth.endDate)
  if (endDate && endOfDay(endDate) < startOfDay(new Date())) {
    return 'expired'
  }

  if (auth.status === 'pending') {
    return 'pending'
  }

  const { utilizationPercent } = calculateAuthorizationUtilization(auth)

  if (auth.status === 'exhausted' || utilizationPercent >= 100) {
    return 'exhausted'
  }

  return 'active'
}

export function getEffectiveAuthorizationStatus(auth: Authorization): AuthorizationStatus {
  return resolveAuthorizationStatus(auth)
}

function getVisitDurationMinutes(visit: Visit): number {
  if (typeof visit.actualDurationMinutes === 'number' && visit.actualDurationMinutes > 0) {
    return visit.actualDurationMinutes
  }

  const actualStart = toDate(visit.actualStart)
  const actualEnd = toDate(visit.actualEnd)
  if (actualStart && actualEnd && actualEnd > actualStart) {
    return Math.round((actualEnd.getTime() - actualStart.getTime()) / (1000 * 60))
  }

  if (typeof visit.scheduledDurationMinutes === 'number' && visit.scheduledDurationMinutes > 0) {
    return visit.scheduledDurationMinutes
  }

  const scheduledStart = toDate(visit.scheduledStart)
  const scheduledEnd = toDate(visit.scheduledEnd)
  if (scheduledStart && scheduledEnd && scheduledEnd > scheduledStart) {
    return Math.round((scheduledEnd.getTime() - scheduledStart.getTime()) / (1000 * 60))
  }

  return 0
}

export function calculateVisitUnitsUsed(auth: Authorization, visit: Visit): number {
  if (auth.unitType === 'visits') {
    return 1
  }

  const durationMinutes = getVisitDurationMinutes(visit)
  if (durationMinutes <= 0) {
    return 0
  }

  // AsiliFlow does not yet store payer-specific conversion rules for generic "units",
  // so both "hours" and "units" currently track against visit duration in hours.
  return Math.round((durationMinutes / 60) * 100) / 100
}

export function calculateAuthorizationUsageFromVisits(
  authorizations: Authorization[],
  visits: Visit[],
  options: {
    rangeStart?: Date
    rangeEnd?: Date
  } = {}
): Map<string, number> {
  const usageByAuthorization = new Map<string, number>()
  const activeAuthorizations = authorizations.filter(auth => auth.isDeleted !== true)

  visits.forEach(visit => {
    if (visit.isDeleted || visit.status !== 'completed') return

    const visitDate = toDate(visit.scheduledStart)
    if (!visitDate) return
    if (options.rangeStart && visitDate < options.rangeStart) return
    if (options.rangeEnd && visitDate > options.rangeEnd) return

    let authorization = visit.authorizationId
      ? activeAuthorizations.find(item => item.id === visit.authorizationId)
      : undefined

    if (!authorization) {
      authorization = activeAuthorizations
        .filter(item => {
          const authStart = toDate(item.startDate)
          const authEnd = toDate(item.endDate)
          if (!authStart || !authEnd) return false
          if (item.clientId !== visit.clientId) return false
          if (visitDate < authStart || visitDate > authEnd) return false
          if (visit.serviceType && item.serviceType) return item.serviceType === visit.serviceType
          return true
        })
        .sort((a, b) => {
          const aStart = toDate(a.startDate)?.getTime() || 0
          const bStart = toDate(b.startDate)?.getTime() || 0
          return bStart - aStart
        })[0]
    }

    if (!authorization) return

    const current = usageByAuthorization.get(authorization.id) || 0
    usageByAuthorization.set(
      authorization.id,
      Math.round((current + calculateVisitUnitsUsed(authorization, visit)) * 100) / 100
    )
  })

  return usageByAuthorization
}

interface CreateAuthorizationParams {
  data: Omit<Authorization, 'id' | 'agencyId' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}

interface UpdateAuthorizationParams {
  authorizationId: string
  data: Partial<Authorization>
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}

interface ArchiveAuthorizationParams {
  authorizationId: string
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}

interface AuthorizationExpirationEmailTestRequest {
  authorizationId: string
  milestoneDays: AuthorizationExpirationMilestone
}

interface AuthorizationExpirationEmailTestResponse {
  success: boolean
  authorizationId: string
  milestoneDays: AuthorizationExpirationMilestone
  emailCount: number
  recipients: Array<{ email: string; recipientType: 'admin' | 'family' }>
  previews: Array<{ recipientType: 'admin' | 'family'; subject: string; html: string }>
}

// Get a single authorization by ID
export async function getAuthorization(authorizationId: string): Promise<Authorization | null> {
  const docRef = doc(db, COLLECTION, authorizationId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as Authorization
}

// Get all authorizations for an agency
export async function getAuthorizations(agencyId: string): Promise<Authorization[]> {
  const q = query(
    collection(db, COLLECTION),
    where('agencyId', '==', agencyId),
    orderBy('endDate', 'asc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() } as Authorization))
    .filter(auth => auth.isDeleted !== true)
}

// Get authorizations for a specific client
export async function getClientAuthorizations(
  agencyId: string,
  clientId: string
): Promise<Authorization[]> {
  const q = query(
    collection(db, COLLECTION),
    where('agencyId', '==', agencyId),
    where('clientId', '==', clientId),
    orderBy('endDate', 'asc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() } as Authorization))
    .filter(auth => auth.isDeleted !== true)
}

// Get active authorizations expiring within N days
export async function getExpiringAuthorizations(
  agencyId: string,
  daysUntilExpiration: number
): Promise<Authorization[]> {
  const authorizations = await getAuthorizations(agencyId)
  const now = new Date()
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() + daysUntilExpiration)

  return authorizations.filter(auth => {
    if (getEffectiveAuthorizationStatus(auth) !== 'active') return false
    const endDate = toDate(auth.endDate)
    return Boolean(endDate && endDate >= now && endDate <= cutoffDate)
  })
}

// Get authorizations with high utilization (>= threshold%)
export async function getHighUtilizationAuthorizations(
  agencyId: string,
  utilizationThreshold: number = 80
): Promise<Authorization[]> {
  const authorizations = await getAuthorizations(agencyId)

  return authorizations.filter(auth => {
    if (getEffectiveAuthorizationStatus(auth) !== 'active') return false
    return calculateAuthorizationUtilization(auth).utilizationPercent >= utilizationThreshold
  })
}

export async function triggerAuthorizationExpirationEmailTest(
  payload: AuthorizationExpirationEmailTestRequest
): Promise<AuthorizationExpirationEmailTestResponse> {
  const fn = httpsCallable<
    AuthorizationExpirationEmailTestRequest,
    AuthorizationExpirationEmailTestResponse
  >(functions, 'triggerAuthorizationExpirationEmailTest')

  const result = await fn(payload)
  return result.data
}

// Create a new authorization
export async function createAuthorization({
  data,
  agencyId,
  userId,
  userEmail,
  userRole,
}: CreateAuthorizationParams): Promise<string> {
  const docData: DocumentData = {
    ...data,
    status: resolveAuthorizationStatus(data),
    agencyId,
    isDeleted: false,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }

  const docRef = await addDoc(collection(db, COLLECTION), docData)

  // Create audit log
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'create',
    resource: 'authorization',
    resourceId: docRef.id,
    description: `Created authorization: ${data.authorizationNumber} for client ${data.clientId}`,
    newValue: docData as Record<string, unknown>,
  })

  return docRef.id
}

// Update an authorization
export async function updateAuthorization({
  authorizationId,
  data,
  agencyId,
  userId,
  userEmail,
  userRole,
}: UpdateAuthorizationParams): Promise<void> {
  const docRef = doc(db, COLLECTION, authorizationId)

  // Get current data for audit log
  const currentDoc = await getDoc(docRef)
  const currentData = currentDoc.data() as Record<string, unknown>
  const normalizedData = {
    ...data,
    status: resolveAuthorizationStatus({ ...currentData, ...data } as Authorization),
  }

  const updateData: DocumentData = {
    ...normalizedData,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }

  await updateDoc(docRef, updateData)

  // Create audit log
  const changedFields = getChangedFields(currentData, { ...currentData, ...normalizedData })
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'update',
    resource: 'authorization',
    resourceId: authorizationId,
    description: `Updated authorization: ${currentData.authorizationNumber}`,
    previousValue: currentData,
    newValue: { ...currentData, ...normalizedData },
    changedFields,
  })
}

export async function archiveAuthorization({
  authorizationId,
  agencyId,
  userId,
  userEmail,
  userRole,
}: ArchiveAuthorizationParams): Promise<void> {
  const docRef = doc(db, COLLECTION, authorizationId)
  const currentDoc = await getDoc(docRef)

  if (!currentDoc.exists()) {
    throw new Error('Authorization not found')
  }

  const currentData = currentDoc.data() as Record<string, unknown>

  await updateDoc(docRef, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: userId,
    archivedAt: serverTimestamp(),
    archivedBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })

  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'archive',
    resource: 'authorization',
    resourceId: authorizationId,
    description: `Archived authorization: ${currentData.authorizationNumber}`,
    previousValue: currentData,
    changedFields: ['isDeleted', 'archivedAt', 'archivedBy'],
  })
}

// Update units used (commonly called after visit completion)
export async function updateUnitsUsed(
  authorizationId: string,
  unitsToAdd: number,
  agencyId: string,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<void> {
  const docRef = doc(db, COLLECTION, authorizationId)
  const currentDoc = await getDoc(docRef)

  if (!currentDoc.exists()) {
    throw new Error('Authorization not found')
  }

  const currentData = currentDoc.data()
  const newUnitsUsed = (currentData.unitsUsed || 0) + unitsToAdd
  const newStatus = resolveAuthorizationStatus({
    ...(currentData as Authorization),
    unitsUsed: newUnitsUsed,
  })

  await updateDoc(docRef, {
    unitsUsed: newUnitsUsed,
    status: newStatus,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })

  // Create audit log
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'update',
    resource: 'authorization',
    resourceId: authorizationId,
    description: `Updated units used: ${currentData.unitsUsed} → ${newUnitsUsed} (${currentData.authorizationNumber})`,
    previousValue: { unitsUsed: currentData.unitsUsed, status: currentData.status },
    newValue: { unitsUsed: newUnitsUsed, status: newStatus },
    changedFields: ['unitsUsed', ...(newStatus !== currentData.status ? ['status'] : [])],
  })
}

// Calculate authorization statistics
export function calculateAuthorizationStats(auth: Authorization): {
  utilizationPercent: number
  displayUtilizationPercent: number
  unitsRemaining: number
  daysUntilExpiration: number | null
  isExpiringSoon: boolean
  isHighUtilization: boolean
  isExhausted: boolean
  effectiveStatus: AuthorizationStatus
} {
  const {
    utilizationPercent,
    displayUtilizationPercent,
    unitsRemaining,
  } = calculateAuthorizationUtilization(auth)
  const effectiveStatus = getEffectiveAuthorizationStatus(auth)

  let daysUntilExpiration: number | null = null
  if (auth.endDate) {
    // Use toDate helper to properly handle Firestore Timestamps
    const endDate = toDate(auth.endDate)
    if (endDate) {
      daysUntilExpiration = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    }
  }

  return {
    utilizationPercent,
    displayUtilizationPercent,
    unitsRemaining,
    daysUntilExpiration,
    isExpiringSoon: effectiveStatus === 'active' && daysUntilExpiration !== null && daysUntilExpiration <= 30 && daysUntilExpiration > 0,
    isHighUtilization: utilizationPercent >= 80,
    isExhausted: effectiveStatus === 'exhausted',
    effectiveStatus,
  }
}

export function getAuthorizationRenewalBand(auth: Authorization): AuthorizationRenewalBand | null {
  const { daysUntilExpiration, effectiveStatus } = calculateAuthorizationStats(auth)

  if (effectiveStatus === 'expired') {
    return 'expired'
  }

  if (effectiveStatus !== 'active' || daysUntilExpiration === null) {
    return null
  }

  if (daysUntilExpiration <= 0) {
    return 'expired'
  }

  if (daysUntilExpiration <= 30) {
    return '30'
  }

  if (daysUntilExpiration <= 60) {
    return '60'
  }

  if (daysUntilExpiration <= 90) {
    return '90'
  }

  return null
}

export function getAuthorizationExpirationAlert(auth: Authorization): {
  variant: 'warning' | 'error' | 'gray'
  label: string
  detail: string
} | null {
  const { daysUntilExpiration } = calculateAuthorizationStats(auth)

  if (daysUntilExpiration === null) {
    return null
  }

  if (daysUntilExpiration <= 0) {
    return {
      variant: 'error',
      label: 'Expired',
      detail: 'Authorization end date has passed',
    }
  }

  if (daysUntilExpiration <= 30) {
    return {
      variant: 'error',
      label: '30-Day Alert',
      detail: `${daysUntilExpiration} day${daysUntilExpiration === 1 ? '' : 's'} left`,
    }
  }

  if (daysUntilExpiration <= 60) {
    return {
      variant: 'warning',
      label: '60-Day Alert',
      detail: `${daysUntilExpiration} days until expiration`,
    }
  }

  if (daysUntilExpiration <= 90) {
    return {
      variant: 'gray',
      label: '90-Day Alert',
      detail: `${daysUntilExpiration} days until expiration`,
    }
  }

  return null
}
